/**
 * Question World Module - Utility Functions
 * Self-contained utility functions for the question_world module
 */

/**
 * Check if a string contains LaTeX/KaTeX math notation
 * @param {string} value - The string to check
 * @returns {number} - Index of first match, -1 if not found
 */
export const shouldUseKatex = (value) => {
    if (!value || typeof value !== 'string') return -1;
    const regex = new RegExp(/\^|\\|\+|_|\{|\}|\$/);
    return value.search(regex);
};

/**
 * Compare two arrays for equality (order-independent)
 * @param {Array} arr1 - First array
 * @param {Array} arr2 - Second array
 * @returns {boolean} - True if arrays are equal
 */
export const compareArray = (arr1, arr2) => {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();

    for (let i = 0; i < sorted1.length; i += 1) {
        let value1 = sorted1[i];
        let value2 = sorted2[i];
        if (typeof value1 === 'string') value1 = value1.normalize();
        if (typeof value2 === 'string') value2 = value2.normalize();
        if (value1 !== value2) return false;
    }
    return true;
};

/**
 * Get a unique key for a question
 * @param {object} question - The question object
 * @param {number} index - The index of the question
 * @returns {string} - A unique key
 */
export const getQuestionKey = (question, index) =>
    question?._id ||
    question?.questionId?._id ||
    question?.question?._id ||
    question?.id ||
    `question-${index}`;

/**
 * Get student data from localStorage
 * @returns {object} - Student object or empty object
 */
export const getStudentFromStorage = () => {
    try {
        return JSON.parse(localStorage.getItem('student') || '{}');
    } catch (error) {
        return {};
    }
};

/**
 * Format duration in minutes to readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted duration
 */
export const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes} mins`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

/**
 * Calculate progress percentage
 * @param {number} answered - Number of answered questions
 * @param {number} total - Total number of questions
 * @returns {number} - Progress percentage (0-100)
 */
export const calculateProgress = (answered, total) => {
    if (!total || total === 0) return 0;
    return Math.round((answered / total) * 100);
};

